import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  MdAssignment, MdCheckCircle, MdCancel, MdRefresh, MdSave, MdPlayArrow,
  MdPause, MdEditNote, MdFlag, MdClose, MdHistory, MdDelete, MdVisibility, MdSearch,
} from 'react-icons/md';
import { Link } from 'react-router-dom';
import api from '../api/axios';
import { useScanStream } from '../lib/useScanStream';
import { useChangeStream } from '../lib/useChangeStream';
import { confirm } from '../lib/dialogs';

const Toast = ({ msg, type, onClose }) => (
  <div style={{
    position: 'fixed', top: 20, right: 20, zIndex: 9999,
    padding: '12px 18px', borderRadius: 8, color: 'white',
    background: type === 'error' ? '#d32f2f' : type === 'warning' ? '#f57c00' : '#2e7d32',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  }}>
    {msg}
    <button onClick={onClose} style={{ marginLeft: 12, background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}>×</button>
  </div>
);

// ─── Location multi-select dropdown ──────────────────────────────────────────
// Replaces the old grid-of-checkboxes with a compact, search-friendly dropdown.
// Selected locations show as removable chips above the trigger so the operator
// always sees what they've picked even when the list is closed.
const LocationMultiSelect = ({ locations, selectedIds, onToggle, onClear, onSelectAll }) => {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const ref = React.useRef(null);

  React.useEffect(() => {
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const selected = locations.filter(l => selectedIds.includes(l.id));
  const filtered = locations.filter(l => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (l.location_name || '').toLowerCase().includes(q)
        || (l.location_code || '').toLowerCase().includes(q);
  });
  const allSelected = locations.length > 0 && selected.length === locations.length;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Trigger / pill row */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          minHeight: 42, border: '1.5px solid var(--border)', borderRadius: 8,
          padding: '6px 36px 6px 8px', background: 'white', cursor: 'pointer',
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6,
          position: 'relative', transition: 'border-color 0.15s',
          borderColor: open ? 'var(--accent)' : 'var(--border)',
          boxShadow: open ? '0 0 0 3px rgba(33,150,243,0.1)' : 'none',
        }}
      >
        {selected.length === 0 ? (
          <span style={{ color: 'var(--text-muted)', padding: '4px 6px' }}>
            {locations.length === 0 ? 'No locations defined yet' : 'Select one or more locations…'}
          </span>
        ) : (
          selected.map(l => (
            <span key={l.id} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'var(--primary)', color: 'white',
              padding: '3px 4px 3px 8px', borderRadius: 14, fontSize: 12, fontWeight: 500,
            }}>
              <span style={{ fontFamily: 'monospace', fontSize: 11, opacity: 0.85 }}>{l.location_code}</span>
              <span>{l.location_name}</span>
              <button
                onClick={(e) => { e.stopPropagation(); onToggle(l.id); }}
                style={{
                  background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
                  width: 18, height: 18, borderRadius: '50%', cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12,
                }}
                title="Remove">×</button>
            </span>
          ))
        )}
        {/* Caret */}
        <span style={{
          position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
          color: 'var(--text-muted)', pointerEvents: 'none', fontSize: 12,
        }}>{open ? '▲' : '▼'}</span>
      </div>

      {/* Dropdown panel */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          zIndex: 50, background: 'white', border: '1px solid var(--border)',
          borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          overflow: 'hidden',
        }}>
          {/* Search + bulk actions */}
          <div style={{
            display: 'flex', gap: 8, alignItems: 'center',
            padding: 8, borderBottom: '1px solid var(--border-light)', background: '#fafbfd',
          }}>
            <input
              autoFocus
              placeholder="Search by name or code…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{
                flex: 1, padding: '7px 10px', border: '1px solid var(--border)',
                borderRadius: 6, fontSize: 13, outline: 'none',
              }}
            />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); allSelected ? onClear() : onSelectAll(); }}
              style={{
                padding: '6px 12px', fontSize: 12, fontWeight: 600,
                background: 'transparent', color: 'var(--accent)',
                border: '1px solid var(--accent)', borderRadius: 6, cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >
              {allSelected ? 'Clear all' : 'Select all'}
            </button>
          </div>

          {/* Options */}
          <div style={{ maxHeight: 280, overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: 16, color: 'var(--text-muted)', fontSize: 13, textAlign: 'center' }}>
                No locations match "{query}"
              </div>
            ) : filtered.map(l => {
              const isSel = selectedIds.includes(l.id);
              return (
                <div
                  key={l.id}
                  onClick={(e) => { e.stopPropagation(); onToggle(l.id); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '9px 12px', cursor: 'pointer', fontSize: 13,
                    background: isSel ? '#e8edf5' : 'transparent',
                    borderBottom: '1px solid var(--border-light)',
                  }}
                  onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = '#f5f7fa'; }}
                  onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}
                >
                  <input
                    type="checkbox" checked={isSel} readOnly
                    style={{ pointerEvents: 'none', accentColor: 'var(--primary)' }}
                  />
                  <span style={{
                    background: 'var(--primary)', color: 'white',
                    padding: '1px 6px', borderRadius: 4, fontSize: 11, fontFamily: 'monospace',
                  }}>{l.location_code}</span>
                  <span style={{ flex: 1 }}>{l.location_name}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{l.asset_count} assets</span>
                </div>
              );
            })}
          </div>

          {/* Footer summary */}
          <div style={{
            padding: '8px 12px', background: '#fafbfd',
            borderTop: '1px solid var(--border-light)',
            fontSize: 11, color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between',
          }}>
            <span>{selected.length} of {locations.length} selected</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setOpen(false); }}
              style={{
                background: 'none', border: 'none', color: 'var(--accent)',
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}
            >Done</button>
          </div>
        </div>
      )}
    </div>
  );
};

const AssetAudit = () => {
  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [includeDescendants, setIncludeDescendants] = useState(false);
  const [auditName, setAuditName] = useState('');
  const [activeAudit, setActiveAudit] = useState(null);
  const [auditItems, setAuditItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [noteModal, setNoteModal] = useState(null);
  const [missingSearch, setMissingSearch] = useState('');
  // L19: explicit modal for multi-location audit conflicts so the user sees
  // exactly what's happening instead of a flash toast + silent auto-resume.
  const [conflictModal, setConflictModal] = useState(null); // { existingAuditId, error }
  // Past audit list — surfaced here so the operator doesn't have to dive into
  // the separate Audit Report page just to see "what audits have I run?".
  const [pastAudits, setPastAudits] = useState([]);
  const [pastFilter, setPastFilter] = useState('all'); // all|in_progress|paused|completed

  // Live updates: when any client (handheld push, manual entry, fixed reader)
  // scans against this audit, the backend broadcasts on `audit:<id>`.
  // We refresh items when the broadcast says counts changed.
  const wsTopic = activeAudit ? `audit:${activeAudit.id}` : null;
  const { lastEvent: wsEvent, connected: wsConnected } = useScanStream(wsTopic);

  // The audit id is mirrored into a ref so the handler below can read it
  // WITHOUT depending on `activeAudit`. Depending on the object is what used to
  // make this loop forever: the updater always spread into a NEW object, that
  // new reference re-fired the effect, which spread again — thousands of
  // GET /api/audit/:id/items and a frozen tab.
  const activeAuditIdRef = useRef(null);
  useEffect(() => {
    activeAuditIdRef.current = activeAudit ? activeAudit.id : null;
  }, [activeAudit]);

  useEffect(() => {
    const auditId = activeAuditIdRef.current;
    if (!wsEvent || !auditId) return;
    setActiveAudit(a => {
      if (!a) return a;
      const scanned = wsEvent.scanned_count ?? a.scanned_assets;
      const missing = wsEvent.missing_count ?? a.missing_assets;
      // Same object when the counts haven't moved → React bails out of the
      // re-render instead of handing us a new reference.
      if (scanned === a.scanned_assets && missing === a.missing_assets) return a;
      return { ...a, scanned_assets: scanned, missing_assets: missing };
    });
    fetchAuditItems(auditId);
    // `fetchAuditItems` is a stable useCallback defined below — naming it here
    // would read it before initialisation, so deps stay on the event alone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsEvent]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchPastAudits = useCallback(async () => {
    try {
      const r = await api.get('/audit');
      setPastAudits(r.data);
    } catch {}
  }, []);

  useEffect(() => {
    api.get('/locations').then(r => setLocations(r.data)).catch(() => {});
    // Resume an existing in-progress / paused audit on page load
    api.get('/audit/active').then(r => {
      if (r.data) {
        setActiveAudit(r.data);
        if (r.data.locations) setSelectedLocations(r.data.locations.map(l => l.id));
        else if (r.data.location_id) setSelectedLocations([r.data.location_id]);
        fetchAuditItemsInline(r.data.id);
      }
    }).catch(() => {});
    fetchPastAudits();
  }, [fetchPastAudits]);

  // Realtime: refresh on any audit/location/asset mutation from any client.
  useChangeStream(() => {
    api.get('/locations').then(r => setLocations(r.data)).catch(() => {});
    if (activeAudit) {
      api.get(`/audit/${activeAudit.id}`).then(r => setActiveAudit(r.data)).catch(() => {});
      fetchAuditItemsInline(activeAudit.id);
    }
    fetchPastAudits();
  }, ['audit', 'audit_items', 'audit_locations', 'locations', 'assets']);

  const fetchAuditItemsInline = async (auditId) => {
    const res = await api.get(`/audit/${auditId}/items`);
    setAuditItems(res.data);
  };

  const fetchAuditItems = useCallback(async (auditId) => {
    const res = await api.get(`/audit/${auditId}/items`);
    setAuditItems(res.data);
  }, []);

  const handleStart = async () => {
    if (selectedLocations.length === 0) { showToast('Pick at least one location', 'error'); return; }
    setLoading(true);
    try {
      const res = await api.post('/audit/start', {
        location_ids: selectedLocations,
        include_descendants: includeDescendants,
        audit_name: auditName || undefined,
      });
      setActiveAudit(res.data);
      await fetchAuditItems(res.data.id);
      showToast('Audit started');
    } catch (err) {
      const data = err.response?.data || {};
      if (data.audit_id) {
        // Don't silently resume — give the user a choice
        setConflictModal({ existingAuditId: data.audit_id, error: data.error });
      } else {
        showToast(data.error || 'Failed to start audit', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResumeConflict = async () => {
    if (!conflictModal) return;
    try {
      const r = await api.get('/audit/active');
      if (r.data) {
        setActiveAudit(r.data);
        if (r.data.locations) setSelectedLocations(r.data.locations.map(l => l.id));
        await fetchAuditItems(r.data.id);
      }
      setConflictModal(null);
      showToast('Resumed existing audit');
    } catch (err) {
      showToast(err.response?.data?.error || 'Resume failed', 'error');
    }
  };

  const handleCancelExisting = async () => {
    if (!conflictModal) return;
    if (!(await confirm('Delete the existing audit and start a fresh one? Any in-progress scans will be lost.'))) return;
    try {
      await api.delete(`/audit/${conflictModal.existingAuditId}`);
      setConflictModal(null);
      // Re-attempt starting the new audit
      handleStart();
    } catch (err) {
      showToast(err.response?.data?.error || 'Cancel failed', 'error');
    }
  };

  const handleScan = useCallback(async (rfidTag) => {
    if (!activeAudit) return;
    try {
      const res = await api.post(`/audit/${activeAudit.id}/scan`, { rfid_tag: rfidTag });
      if (res.data.status === 'scanned') {
        setActiveAudit(a => ({ ...a,
          scanned_assets: res.data.scanned_count,
          missing_assets: res.data.missing_count,
        }));
        await fetchAuditItems(activeAudit.id);
      }
    } catch (err) {
      if (err.response?.status === 409) showToast(err.response.data.error, 'warning');
    }
  }, [activeAudit, fetchAuditItems]);

  const handlePause = async () => {
    try {
      const res = await api.post(`/audit/${activeAudit.id}/pause`);
      setActiveAudit(res.data);
      showToast('Audit paused — work resumes anytime');
    } catch (err) {
      showToast(err.response?.data?.error || 'Pause failed', 'error');
    }
  };
  const handleResume = async () => {
    try {
      const res = await api.post(`/audit/${activeAudit.id}/resume`);
      setActiveAudit(res.data);
      showToast('Audit resumed');
    } catch (err) {
      showToast(err.response?.data?.error || 'Resume failed', 'error');
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/audit/${activeAudit.id}/complete`);
      showToast(`Done. Scanned: ${res.data.scanned_assets}, Missing: ${res.data.missing_assets}`);
      setActiveAudit(null);
      setAuditItems([]);
      setSelectedLocations([]);
      setAuditName('');
    } catch {
      showToast('Failed to complete audit', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!(await confirm('Cancel and delete this audit?'))) return;
    try {
      await api.delete(`/audit/${activeAudit.id}`);
      setActiveAudit(null);
      setAuditItems([]);
      setSelectedLocations([]);
      setAuditName('');
      showToast('Audit cancelled');
    } catch {
      showToast('Failed to cancel audit', 'error');
    }
  };

  const handleSaveNote = async () => {
    if (!noteModal) return;
    try {
      await api.patch(`/audit/${activeAudit.id}/items/${noteModal.id}`, {
        missing_note: noteModal.missing_note,
        follow_up_required: noteModal.follow_up_required,
        follow_up_resolved: noteModal.follow_up_resolved,
      });
      showToast('Note saved');
      setNoteModal(null);
      fetchAuditItems(activeAudit.id);
    } catch (err) {
      showToast(err.response?.data?.error || 'Save failed', 'error');
    }
  };

  const toggleLocation = (id) => {
    setSelectedLocations(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // Resume a paused / in-progress audit straight from the past-audits list.
  // Server returns the same shape /audit/active does, so we can drop it
  // straight into activeAudit and load its items.
  const handleResumePast = async (audit) => {
    if (audit.status === 'completed') {
      showToast('This audit is already completed — open the Audit Report tab to review it.', 'warning');
      return;
    }
    try {
      const res = await api.get(`/audit/${audit.id}`);
      setActiveAudit(res.data);
      if (res.data.locations) setSelectedLocations(res.data.locations.map(l => l.id));
      else if (res.data.location_id) setSelectedLocations([res.data.location_id]);
      await fetchAuditItems(audit.id);
      showToast(`Resumed "${audit.audit_name}"`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      showToast(err.response?.data?.error || 'Resume failed', 'error');
    }
  };

  const handleDeletePast = async (audit) => {
    if (!(await confirm(`Delete audit "${audit.audit_name}"? This removes all of its scan history. Completed audits should usually be kept for reporting.`))) return;
    try {
      await api.delete(`/audit/${audit.id}`);
      if (activeAudit?.id === audit.id) {
        setActiveAudit(null);
        setAuditItems([]);
      }
      fetchPastAudits();
      showToast('Audit deleted');
    } catch (err) {
      showToast(err.response?.data?.error || 'Delete failed', 'error');
    }
  };

  const filteredPast = pastAudits.filter(a =>
    pastFilter === 'all' ? true : a.status === pastFilter
  );

  const statusBadge = (status) => {
    const map = {
      in_progress: { label: 'IN PROGRESS', bg: '#e3f2fd', fg: '#1565c0' },
      paused:      { label: 'PAUSED',      bg: '#fff3e0', fg: '#ef6c00' },
      completed:   { label: 'COMPLETED',   bg: '#e8f5e9', fg: '#2e7d32' },
    };
    const m = map[status] || { label: status?.toUpperCase() || '—', bg: '#eee', fg: '#555' };
    return (
      <span style={{
        padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700,
        background: m.bg, color: m.fg, letterSpacing: 0.4,
      }}>{m.label}</span>
    );
  };

  const scannedItems = auditItems.filter(i => i.scanned);
  const missingItems = auditItems.filter(i => !i.scanned);
  // Missing-list search across name / RFID / asset tag / serial.
  const missingQuery = missingSearch.trim().toLowerCase();
  const missingFiltered = missingQuery
    ? missingItems.filter(i =>
        [i.asset_name, i.rfid_tag, i.asset_tag, i.serial_number]
          .some(v => String(v || '').toLowerCase().includes(missingQuery)))
    : missingItems;
  // Manually mark a missing item as scanned (fallback when not using the reader).
  // Reuses the same scan endpoint the handheld hits.
  const handleMarkScanned = async (item) => {
    if (!item.rfid_tag) { showToast('This asset has no RFID tag — cannot mark scanned', 'warning'); return; }
    await handleScan(item.rfid_tag);
  };
  const total = auditItems.length;
  const progress = total > 0 ? Math.round((scannedItems.length / total) * 100) : 0;
  const isPaused = activeAudit?.status === 'paused';

  return (
    <div className="page-container">
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <div className="page-header">
        <div>
          <div className="page-title"><MdAssignment /> Asset Audit</div>
          <div className="page-subtitle">RFID-based audits. Pick one or multiple locations.</div>
        </div>
      </div>

      {/* Setup or Active Header */}
      <div className="card" style={{ marginBottom: 16 }}>
        {!activeAudit ? (
          <div>
            <div style={{ marginBottom: 12 }}>
              <label className="form-label">Locations to audit</label>
              <LocationMultiSelect
                locations={locations}
                selectedIds={selectedLocations}
                onToggle={toggleLocation}
                onClear={() => setSelectedLocations([])}
                onSelectAll={() => setSelectedLocations(locations.map(l => l.id))}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={includeDescendants}
                    onChange={e => setIncludeDescendants(e.target.checked)} />
                  Include sub-locations
                </label>
                <input
                  className="form-control" style={{ flex: 1, minWidth: 220 }}
                  placeholder="Audit name (optional — auto-generated if blank)"
                  value={auditName}
                  onChange={e => setAuditName(e.target.value)}
                />
                <button className="btn btn-primary" onClick={handleStart} disabled={loading || selectedLocations.length === 0}>
                  <MdPlayArrow /> {loading ? 'Starting…' : `Start Audit${selectedLocations.length > 1 ? ` (${selectedLocations.length} locations)` : ''}`}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
              <span style={{
                padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 700,
                background: isPaused ? '#fff3e0' : '#e3f2fd',
                color: isPaused ? '#ef6c00' : '#1565c0',
              }}>
                {isPaused ? '⏸ PAUSED' : '▶ IN PROGRESS'} — {activeAudit.audit_name}
              </span>
              {activeAudit.locations && activeAudit.locations.length > 0 && (
                <span style={{ fontSize: 12, color: '#666' }}>
                  {activeAudit.locations.map(l => l.location_name).join(' · ')}
                </span>
              )}
              <span style={{
                padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700,
                background: wsConnected ? '#e8f5e9' : '#fff3e0',
                color: wsConnected ? '#2e7d32' : '#ef6c00',
                letterSpacing: 0.4,
              }} title={wsConnected ? 'Live updates from any reader/handheld pushing scans' : 'WebSocket disconnected — page will refresh on click'}>
                {wsConnected ? '⚡ LIVE' : '⟳ POLLING'}
              </span>
              <button className="btn btn-accent btn-sm" onClick={() => fetchAuditItems(activeAudit.id)}><MdRefresh /></button>
              {!isPaused ? (
                <button className="btn btn-warning btn-sm" onClick={handlePause}><MdPause /> Pause</button>
              ) : (
                <button className="btn btn-success btn-sm" onClick={handleResume}><MdPlayArrow /> Resume</button>
              )}
              <button className="btn btn-success" onClick={handleComplete} disabled={loading || isPaused}>
                <MdSave /> Complete
              </button>
              <button className="btn btn-danger btn-sm" onClick={handleDelete}>
                <MdCancel /> Cancel
              </button>
            </div>

            <div style={{ display: 'flex', gap: 24, marginBottom: 8, flexWrap: 'wrap' }}>
              {[
                { label: 'Total',    value: total, color: 'var(--primary)' },
                { label: 'Scanned',  value: scannedItems.length, color: 'var(--success)' },
                { label: 'Missing',  value: missingItems.length, color: 'var(--danger)' },
                { label: 'Progress', value: `${progress}%`, color: 'var(--accent)' },
              ].map(s => (
                <div key={s.label} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 500 }}>{s.label}</div>
                </div>
              ))}
            </div>
            <div className="progress-bar-wrap">
              <div className="progress-bar-fill" style={{
                width: `${progress}%`,
                background: progress === 100 ? 'var(--success)' : 'var(--accent)',
              }} />
            </div>
          </div>
        )}
      </div>

      {activeAudit && (
        <div className="grid-audit" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>
          {/* Scanned */}
          <div className="card">
            <div className="card-header">
              <div className="card-title" style={{ color: 'var(--success)' }}>
                <MdCheckCircle /> Scanned ({scannedItems.length})
              </div>
            </div>
            <div style={{ maxHeight: 380, overflowY: 'auto' }}>
              {scannedItems.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)', fontSize: 13 }}>No scans yet</div>
              ) : scannedItems.map(item => (
                <div key={item.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 12px', borderBottom: '1px solid var(--border-light)',
                  background: '#f0fff4',
                }}>
                  <MdCheckCircle style={{ color: 'var(--success)', fontSize: 18, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.asset_name || 'Unknown'}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{item.rfid_tag}</div>
                  </div>
                  {item.scanned_at && <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{new Date(item.scanned_at).toLocaleTimeString()}</div>}
                </div>
              ))}
            </div>
          </div>

          {/* Missing */}
          <div className="card">
            <div className="card-header">
              <div className="card-title" style={{ color: 'var(--danger)' }}>
                <MdCancel /> Missing ({missingFiltered.length}{missingQuery ? ` of ${missingItems.length}` : ''})
              </div>
            </div>
            {/* Search within the missing list */}
            <div style={{ position: 'relative', marginBottom: 8 }}>
              <MdSearch style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input
                className="form-control"
                style={{ paddingLeft: 32 }}
                placeholder="Search missing by name, RFID, tag, serial…"
                value={missingSearch}
                onChange={e => setMissingSearch(e.target.value)}
              />
            </div>
            <div style={{ maxHeight: 380, overflowY: 'auto' }}>
              {missingItems.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 32, color: 'var(--success)', fontSize: 13, fontWeight: 500 }}>
                  All assets accounted for!
                </div>
              ) : missingFiltered.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)', fontSize: 13 }}>
                  No missing items match “{missingSearch}”
                </div>
              ) : missingFiltered.map(item => (
                <div key={item.id} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  padding: '8px 12px', borderBottom: '1px solid var(--border-light)',
                  background: '#fff5f5',
                }}>
                  <MdCancel style={{ color: 'var(--danger)', fontSize: 18, flexShrink: 0, marginTop: 2 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.asset_name || 'Unknown'}
                      {item.follow_up_required ? <MdFlag style={{ color: '#f57c00', verticalAlign: 'middle', marginLeft: 6 }} title="Follow-up required" /> : null}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{item.rfid_tag}</div>
                    {item.missing_note && (
                      <div style={{ fontSize: 11, color: '#777', fontStyle: 'italic', marginTop: 3 }}>
                        “{item.missing_note}”
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    <button
                      className="btn btn-success btn-sm"
                      title="Mark this asset as scanned / found"
                      onClick={() => handleMarkScanned(item)}
                    ><MdCheckCircle /> Scanned</button>
                    <button
                      className="btn btn-outline btn-icon btn-sm"
                      title="Add note / follow-up"
                      onClick={() => setNoteModal({ ...item })}
                    ><MdEditNote /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Missing note modal */}
      {/* Conflict modal — explains what to do when start fails because another audit owns these locations */}
      {conflictModal && (
        <div className="modal-overlay" onClick={() => setConflictModal(null)}>
          <div className="modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">⚠ Audit conflict</div>
              <button className="btn btn-icon" onClick={() => setConflictModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p style={{ marginTop: 0 }}>
                {conflictModal.error || 'An audit is already running on at least one of the selected locations.'}
              </p>
              <p style={{ fontSize: 13, color: '#444' }}>
                Existing audit: <strong>#{conflictModal.existingAuditId}</strong>
              </p>
              <p style={{ fontSize: 13, color: '#666' }}>
                Pick what you want to do:
              </p>
              <ul style={{ fontSize: 13, color: '#444', lineHeight: 1.7 }}>
                <li><strong>Resume existing</strong> — load the in-progress audit and keep scanning</li>
                <li><strong>Cancel + start new</strong> — delete the existing audit and create a fresh one (any prior scans on it are lost)</li>
              </ul>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setConflictModal(null)}>Do nothing</button>
              <button className="btn btn-danger" onClick={handleCancelExisting}>Cancel + start new</button>
              <button className="btn btn-primary" onClick={handleResumeConflict}>Resume existing</button>
            </div>
          </div>
        </div>
      )}

      {noteModal && (
        <div className="modal-overlay" onClick={() => setNoteModal(null)}>
          <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title"><MdEditNote /> Note for {noteModal.asset_name || 'item'}</div>
              <button className="btn btn-icon" onClick={() => setNoteModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Why is this asset missing?</label>
                <textarea className="form-control" rows={3}
                  placeholder="e.g. Sent for repair, possibly relocated to Floor 2, etc."
                  value={noteModal.missing_note || ''}
                  onChange={e => setNoteModal(m => ({ ...m, missing_note: e.target.value }))}
                />
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!noteModal.follow_up_required}
                    onChange={e => setNoteModal(m => ({ ...m, follow_up_required: e.target.checked }))} />
                  Follow-up required
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!noteModal.follow_up_resolved}
                    onChange={e => setNoteModal(m => ({ ...m, follow_up_resolved: e.target.checked }))} />
                  Resolved
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setNoteModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveNote}><MdSave /> Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Past audits — surfaced inline so the operator can see prior sessions
          and resume / review without leaving this page. The full Audit Report
          view (with multi-sheet exports and detail per item) is still on the
          /audit-report page; this is just the fast index. */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header">
          <div className="card-title">
            <MdHistory style={{ color: 'var(--accent)' }} /> Past Audits
            <span className="badge badge-info" style={{ marginLeft: 8 }}>{filteredPast.length}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {[
              { k: 'all',         label: 'All' },
              { k: 'in_progress', label: 'In Progress' },
              { k: 'paused',      label: 'Paused' },
              { k: 'completed',   label: 'Completed' },
            ].map(t => (
              <button
                key={t.k}
                className={`btn btn-sm ${pastFilter === t.k ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => setPastFilter(t.k)}
              >{t.label}</button>
            ))}
            <button className="btn btn-outline btn-sm" onClick={fetchPastAudits} title="Refresh"><MdRefresh /></button>
          </div>
        </div>

        {filteredPast.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            {pastAudits.length === 0
              ? 'No audits yet. Pick locations above and tap Start Audit.'
              : 'No audits match this filter.'}
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Name</th>
                  <th>Locations</th>
                  <th>Started</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                  <th style={{ textAlign: 'right' }}>Scanned</th>
                  <th style={{ textAlign: 'right' }}>Missing</th>
                  <th>Auditor</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPast.map(a => {
                  const locText = a.locations?.length
                    ? a.locations.map(l => l.location_name).join(' · ')
                    : (a.location_name || '—');
                  const isResumable = a.status === 'paused' || a.status === 'in_progress';
                  const isActiveOne = activeAudit?.id === a.id;
                  return (
                    <tr key={a.id} style={isActiveOne ? { background: '#e3f2fd' } : undefined}>
                      <td>{statusBadge(a.status)}</td>
                      <td style={{ fontWeight: 500 }}>
                        {a.audit_name || `Audit #${a.id}`}
                        {isActiveOne && (
                          <span style={{ marginLeft: 8, fontSize: 10, color: '#1565c0', fontWeight: 700 }}>
                            ← active
                          </span>
                        )}
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{locText}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {a.audit_date ? new Date(a.audit_date).toLocaleString() : '—'}
                      </td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{a.total_assets ?? 0}</td>
                      <td style={{ textAlign: 'right', color: 'var(--success)', fontVariantNumeric: 'tabular-nums' }}>{a.scanned_assets ?? 0}</td>
                      <td style={{ textAlign: 'right', color: 'var(--danger)', fontVariantNumeric: 'tabular-nums' }}>{a.missing_assets ?? 0}</td>
                      <td style={{ fontSize: 12 }}>{a.created_by_name || '—'}</td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: 4 }}>
                          {isResumable && !isActiveOne && (
                            <button
                              className="btn btn-primary btn-sm"
                              title="Resume this audit and load its items"
                              onClick={() => handleResumePast(a)}
                            ><MdPlayArrow /> Resume</button>
                          )}
                          <Link
                            to={`/audit-report?audit_id=${a.id}`}
                            className="btn btn-outline btn-sm"
                            title="Open full report (per-location stats, scan trail, exports)"
                          ><MdVisibility /> Report</Link>
                          <button
                            className="btn btn-danger btn-icon btn-sm"
                            title="Delete this audit"
                            onClick={() => handleDeletePast(a)}
                          ><MdDelete /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!activeAudit && selectedLocations.length === 0 && pastAudits.length === 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="empty-state">
            <MdAssignment />
            <h3>No Active Audit</h3>
            <p>Select one or more locations above and click "Start Audit" to begin scanning assets.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetAudit;
